<template>
  <b-card :border-variant="acceptBets ? 'success' : 'danger'" class="mb-0">
    <!-- Password Confirm Modal -->
    <b-modal
      v-model="confirmModal.show"
      title="Confirm Action"
      centered
      hide-footer
    >
      <p class="text-black">
        Enter password to
        <strong>{{ confirmModal.nextValue ? 'enable' : 'disable' }} System Accept Bets</strong>
      </p>
      <b-form-input
        v-model="confirmModal.password"
        type="password"
        placeholder="Enter password"
        class="mb-2"
        @keyup.enter="applyToggle"
      />
      <template v-if="!confirmModal.nextValue">
        <label class="mb-25 text-black font-weight-bold">Turn On At</label>
        <div class="d-flex mb-2" style="gap: 8px">
          <b-form-input
            v-model="confirmModal.turnOnAtDate"
            type="date"
            style="flex: 1"
          />
          <b-form-select
            v-model="confirmModal.turnOnAtHour"
            :options="hourOptions"
            style="width: 90px"
          />
        </div>
      </template>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" @click="cancelToggle">
          Cancel
        </b-button>
        <b-button variant="danger" :disabled="saving || !confirmModal.password" @click="applyToggle">
          <b-spinner v-if="saving" small class="mr-50" />
          Confirm
        </b-button>
      </div>
    </b-modal>

    <!-- Card body -->
    <div v-if="loading" class="text-center py-2">
      <b-spinner variant="primary" />
    </div>
    <div v-else class="d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center" style="gap: 10px">
        <span class="status-dot" :class="acceptBets ? 'bg-success' : 'bg-danger'" />
        <div>
          <p class="font-weight-bolder mb-0">SYSTEM ACCEPT BETS</p>
          <small :class="acceptBets ? 'text-success' : 'text-danger'">
            Bets are currently {{ acceptBets ? 'ACCEPTED' : 'STOPPED' }}
          </small>
          <small v-if="!acceptBets && turnOnAt" class="text-muted d-block">
            Turn on at: {{ formattedTurnOnAt }}
          </small>
        </div>
      </div>
      <b-form-checkbox
        v-model="acceptBets"
        switch
        size="lg"
        :disabled="saving || loading"
        @change="onToggle"
      />
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BModal,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BModal,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BSpinner,
  },
  data() {
    return {
      acceptBets: false,
      turnOnAt: null,
      confirmModal: {
        show: false,
        nextValue: null,
        password: "",
        turnOnAtDate: null,
        turnOnAtHour: null,
      },
      hourOptions: [
        { value: null, text: 'Hour' },
        ...Array.from({ length: 24 }, (_, i) => ({ value: i, text: String(i).padStart(2, '0') + ':00' })),
      ],
    };
  },
  computed: {
    ...mapState("settings/system-accept-bets", ["loading", "saving"]),
    formattedTurnOnAt() {
      if (!this.turnOnAt) return null;
      const d = new Date(this.turnOnAt);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', hour12: false });
    },
  },
  created() {
    this.fetchConfig();
  },
  methods: {
    ...mapActions("settings/system-accept-bets", [
      "getSystemAcceptBets",
      "updateSystemAcceptBets",
    ]),
    async fetchConfig() {
      const data = await this.getSystemAcceptBets();
      if (data?.value) {
        this.acceptBets = data.value.acceptBets ?? false;
        this.turnOnAt = data.value.turnOnAt ?? null;
      }
    },
    onToggle(value) {
      // Revert immediately — only apply after password confirm
      this.acceptBets = !value;
      this.confirmModal = { show: true, nextValue: value, password: "", turnOnAtDate: null, turnOnAtHour: null };
    },
    async applyToggle() {
      if (!this.confirmModal.password) return;
      const { nextValue, password, turnOnAtDate, turnOnAtHour } = this.confirmModal;
      const turnOnAt = !nextValue && turnOnAtDate && turnOnAtHour !== null
        ? new Date(`${turnOnAtDate}T${String(turnOnAtHour).padStart(2, '0')}:00:00`).toISOString()
        : undefined;
      const result = await this.updateSystemAcceptBets({
        acceptBets: nextValue,
        ...(turnOnAt !== undefined && { turnOnAt }),
        password,
      });
      if (result) {
        this.acceptBets = nextValue;
        this.turnOnAt = turnOnAt ?? null;
        this.showToastSuccess(
          "System Accept Bets Updated",
          `Bet acceptance has been ${nextValue ? "enabled" : "disabled"}.`
        );
      }
      this.confirmModal.show = false;
      this.confirmModal.password = "";
      this.confirmModal.turnOnAtDate = null;
      this.confirmModal.turnOnAtHour = null;
    },
    cancelToggle() {
      this.confirmModal.show = false;
      this.confirmModal.password = "";
      this.confirmModal.turnOnAtDate = null;
      this.confirmModal.turnOnAtHour = null;
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
